import {
    createBrowserRouter,
    Navigate,
    RouterProvider,
} from "react-router-dom";
import Root from "./layout/Root/Root";
import KeyGenerator from "./pages/KeyGenerator/KeyGenerator";
import LicenseKeys from "./pages/LicenseKeys/LicenseKeys";
import IoloLicenseKeys from "./pages/IoloLicenseKeys/IoloLicenseKeys";
import Products from "./pages/Products/Products";
import Projects from "./pages/Projects/Projects";
import Users from "./pages/Users/Users";
import MigratedProducts from "./pages/Products/MigratedProducts/MigratedProducts";
import LicenseKeysMigrated from "./pages/LicenseKeys/LicenseKeysMigrated/LicenseKeysMigrated";
import Auth from "./pages/Auth/Auth";
import AuthCallback from "./pages/Auth/AuthCallback";
import ProtectedRoute from "./ProtectedRoute";
import { FC } from "react";
import { ThemeProvider } from "./ThemeContext";
import HistoryPage from "./pages/History/History";
import LicenseKeysNotActivated from "./pages/LicenseKeys/Pages/LicenseKeysNotActivated/LicenseKeysNotActivated";
import Transactions from "./pages/Transactions/Transactions";
import PageTitle from "./assets/components/PageTitle/PageTitle";
import { ROUTE_ACCESS, readAccessSubject, resolveLandingPath } from "./access";

// Access rules per area live in access.ts and are shared with the nav panel.
// transactions has no permission of its own and stays open to any authenticated
// user (it is not in the nav panel either).

const NoAccess: FC = () => (
    <div className="page_with_table">
        <PageTitle text="No access" />
        <p>
            This account has no permissions for any section. Please contact an
            administrator.
        </p>
    </div>
);

const DefaultRedirect: FC = () => {
    const target = resolveLandingPath(readAccessSubject());

    // Landing on a route the user cannot open would bounce back to "/" and
    // redirect again — show a message instead of navigating.
    if (target === undefined) {
        return <NoAccess />;
    }

    return <Navigate to={target} replace />;
};

const router = createBrowserRouter([
    {
        path: "/",
        element: (
            <ProtectedRoute>
                <Root />
            </ProtectedRoute>
        ),
        children: [
            {
                path: "/",
                element: <DefaultRedirect />,
            },
            {
                path: "key-generator",
                element: (
                    <ProtectedRoute access={ROUTE_ACCESS.KEY_GENERATOR}>
                        <KeyGenerator />
                    </ProtectedRoute>
                ),
            },
            {
                path: "license-keys",
                children: [
                    {
                        path: "actual",
                        element: (
                            <ProtectedRoute access={ROUTE_ACCESS.LICENSE_KEYS}>
                                <LicenseKeys />
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: "migrated",
                        element: (
                            <ProtectedRoute access={ROUTE_ACCESS.LICENSE_KEYS}>
                                <LicenseKeysMigrated />
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: "not-activated",
                        element: (
                            <ProtectedRoute access={ROUTE_ACCESS.LICENSE_KEYS}>
                                <LicenseKeysNotActivated />
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: "iolo",
                        element: (
                            <ProtectedRoute
                                access={ROUTE_ACCESS.LICENSE_KEYS_IOLO}
                            >
                                <IoloLicenseKeys />
                            </ProtectedRoute>
                        ),
                    },
                ],
            },
            {
                path: "products",
                children: [
                    {
                        path: "actual",
                        element: (
                            <ProtectedRoute access={ROUTE_ACCESS.PRODUCTS}>
                                <Products />
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: "migrated",
                        element: (
                            <ProtectedRoute access={ROUTE_ACCESS.PRODUCTS}>
                                <MigratedProducts />
                            </ProtectedRoute>
                        ),
                    },
                ],
            },
            {
                path: "projects",
                element: (
                    <ProtectedRoute access={ROUTE_ACCESS.PROJECTS}>
                        <Projects />
                    </ProtectedRoute>
                ),
            },
            {
                path: "users",
                element: (
                    <ProtectedRoute access={ROUTE_ACCESS.USERS}>
                        <Users />
                    </ProtectedRoute>
                ),
            },
            {
                path: "history",
                element: (
                    <ProtectedRoute access={ROUTE_ACCESS.HISTORY}>
                        <HistoryPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: "transactions",
                element: <Transactions />,
            },
        ],
    },
    {
        path: "auth",
        element: <Auth />,
    },
    {
        // Public route — must stay outside ProtectedRoute so the token from the
        // Microsoft redirect can be read before the auth gate runs.
        path: "auth/callback",
        element: <AuthCallback />,
    },
]);

const App: FC = () => {
    return (
        <ThemeProvider>
            <RouterProvider router={router} />
        </ThemeProvider>
    );
};

export default App;
